import React, { useEffect, useState } from 'react';

import AttachMoneyIcon from '@mui/icons-material/AttachMoneyOutlined';
import BookmarkIcon from '@mui/icons-material/BookmarkOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import Breadcrumbs from '../../../components/Breadcrumbs';
import PageContent from '../../../components/PageContent';
import { LoadingWrapper } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import Tabs from '../../../components/Tabs';
import {
  useGetCourseQuery,
} from '../../../services/courses';
import { SUBJECTS } from '../../../utils/common/constants';
import useTab from '../../../utils/react/hooks/useTab';
import {
  ContentContainer, PageContainer,
} from '../index.styles';
import LessonDetails from '../LessonDetails';

import BillingTab from './BillingTab';
import DetailsTab from './DetailsTab';
import LessonsTab from './LessonsTab';
import SettingsTab from './SettingsTab';

const TAB_OPTIONS = [
  {
    key: 'details',
    label: 'Details',
    icon: <BookmarkIcon />,
  },
  {
    key: 'lessonTemplates',
    label: 'Lessons',
    icon: <MenuBookIcon />,
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: <AttachMoneyIcon />,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <SettingsOutlinedIcon />,
  },
];

interface CoursePageProps {
  subject: string,
  course: string
}

const CoursePage = ({ subject, course }: CoursePageProps) => {
  const [tab, setTab] = useTab({ default: TAB_OPTIONS[0].key });
  const [lesson, setLesson] = useState<string | undefined>(undefined);

  const { data: courseData, isLoading: isCourseLoading } = useGetCourseQuery(
    { courseId: course }, { skip: !subject || !course },
  );

  const handleTabChange = (key: string) => {
    setTab(key);
  };

  useEffect(() => {
    // Close lesson details sidebar when switching out of lessons tab
    if (tab !== 'lessonTemplates') {
      setLesson(undefined);
    }
  }, [tab]);

  if (!courseData) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <PageContent
      sideBarOpen={!!lesson}
      sideBarWidth={600}
      sideBar={(
        <LessonDetails
          course={course}
          lesson={lesson}
          onClose={() => setLesson(undefined)}
        />
      )}
    >
      <PageContainer>
        <LoadingWrapper loading={isCourseLoading}>
          <ContentContainer direction="column" spacing={2}>
            <Breadcrumbs
              title={courseData.data.shortLabel}
              path={[
                {
                  label: SUBJECTS[subject]?.label,
                  href: `/admin/subjects/${subject}`,
                },
              ]}
            />
            <Tabs options={TAB_OPTIONS} selectedKey={tab} onChange={handleTabChange} />
            {
              tab === 'details' && <DetailsTab course={courseData.data} courseMetadata={courseData.metadata} />
            }
            {
              tab === 'lessonTemplates' && <LessonsTab course={courseData.data} onLessonClick={(lessonId) => setLesson(lessonId)} />
            }
            {
              tab === 'billing' && <BillingTab data={courseData.data} metadata={courseData.metadata} />
            }
            {
              tab === 'settings' && <SettingsTab course={courseData.data} />
            }
          </ContentContainer>
        </LoadingWrapper>
      </PageContainer>
    </PageContent>
  );
};

export default CoursePage;
